// Vendors tab.
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState } from 'react';
import { useApi, putJson, delJson } from '../../api.js';

// ============================================================================
//  VendorsPanel — receiving-site reference (2026-07-07)
// ============================================================================
//
//  Lists everything currently in the vendors table PLUS destinations
//  discovered in loads_outgoing that haven't been categorized yet
//  (returned as `unclassified` by /api/vendors GET). Each row shows the
//  vendor's display name, waste-type category, and how many outgoing
//  loads have hit that destination overall.
//
//  Suggested waste types are baked into a datalist ('Sand', 'Clay',
//  'Stone', 'Clay/Stone') but any string is allowed — ops shouldn't
//  need a code push to introduce a new category.
// ============================================================================

export default function VendorsPanel({ canWrite = true }) {
  // Data: vendors (dump sites + haulers) and QuickBooks spend for the last 12 months.
  const { data, error, loading, refresh } = useApi('/api/vendors');
  // QBO trailing-12mo spend by vendor — fetched separately from the
  // existing vendor-spend endpoint. Matched by fuzzy lowercase name so
  // the panel can show a $ column even before qbo_vendor_id is set.
  const qboSpend = useApi('/api/vendor-spend?period=trailing12');
  const [edit, setEdit] = useState(null);   // { display_name, vendor_type, waste_type, notes, canonical_name? }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');   // all | dump_site | hauler | both

  if (loading && !data) return <div className="loading">Loading vendors…</div>;
  if (error)             return <div className="error">Error: {error}</div>;
  if (!data)             return null;

  // Lists from the API: saved vendors and names seen in outgoing loads that are not classified yet.
  const vendors             = data.vendors || [];
  const unclassifiedDests   = data.unclassified || [];          // dump-site candidates
  const unclassifiedHaulers = data.unclassifiedHaulers || [];   // hauler candidates
  const tableMissing        = data.tableExists === false;

  // QBO spend lookup by fuzzy name. Any local vendor whose lowercase
  // canonical or explicit qbo_vendor_id / qbo_display_name matches a QBO
  // vendor gets its trailing-12mo total spend shown.
  const qboRows = (qboSpend.data && qboSpend.data.vendors) || [];
  const qboByLower = new Map();
  for (const q of qboRows) qboByLower.set(String(q.vendor).toLowerCase().trim(), q);
  function qboFor(v) {
    if (v.qbo_display_name) {
      const hit = qboByLower.get(String(v.qbo_display_name).toLowerCase().trim());
      if (hit) return hit;
    }
    const hit = qboByLower.get(v.canonical_name);
    if (hit) return hit;
    // Fuzzy: any QBO vendor whose lowercased name STARTS WITH the local
    // canonical (helps when QBO has "Ashgrove Landfill Inc." for "Ashgrove").
    for (const [k, q] of qboByLower.entries()) {
      if (k.startsWith(v.canonical_name + ' ') || k === v.canonical_name) return q;
    }
    return null;
  }

  // Helpers: waste-type suggestions, filters, type labels, money format.
  // Distinct existing waste types for the datalist (plus a default set).
  const knownTypes = Array.from(new Set([
    'Sand', 'Clay', 'Stone', 'Clay/Stone', 'Mixed',
    ...vendors.map(v => (v.waste_type || '').trim()).filter(Boolean)
  ])).sort();

  const f = filter.trim().toLowerCase();
  const showV = vendors.filter(v => {
    if (typeFilter !== 'all' && (v.vendor_type || 'dump_site') !== typeFilter && !(typeFilter === 'dump_site' && v.vendor_type === 'both') && !(typeFilter === 'hauler' && v.vendor_type === 'both')) return false;
    if (!f) return true;
    return (v.display_name || '').toLowerCase().includes(f)
        || (v.waste_type   || '').toLowerCase().includes(f);
  });

  const VENDOR_TYPE_LABELS = {
    dump_site: 'Dump site',
    hauler:    'Hauler',
    both:      'Both'
  };
  function typeChip(vt) {
    const t = vt || 'dump_site';
    const bg = t === 'hauler' ? '#fef3c7' : t === 'both' ? '#e0e7ff' : '#dcfce7';
    const fg = t === 'hauler' ? '#92400e' : t === 'both' ? '#3730a3' : '#166534';
    return <span style={{ padding: '0.1rem 0.45rem', background: bg, color: fg,
                           borderRadius: 3, fontSize: '0.75em', fontWeight: 600 }}>
      {VENDOR_TYPE_LABELS[t] || t}
    </span>;
  }
  const money = (n) => n == null ? '—' : '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  // Actions: save / delete a vendor.
  async function save() {
    if (!edit || !edit.display_name) { alert('Display name is required.'); return; }
    setBusy(true); setMsg(null);
    try {
      await putJson('/api/vendors', edit);
      setEdit(null);
      setMsg({ kind: 'ok', text: 'Saved ' + edit.display_name });
      refresh();
    } catch (e) {
      setMsg({ kind: 'error', text: 'Save failed: ' + e.message });
    } finally { setBusy(false); }
  }

  async function del(v) {
    if (!confirm('Delete vendor "' + v.display_name + '"? Loads that reference this destination are NOT deleted.')) return;
    setBusy(true); setMsg(null);
    try {
      await delJson('/api/vendors/' + encodeURIComponent(v.canonical_name));
      setMsg({ kind: 'ok', text: 'Deleted ' + v.display_name });
      refresh();
    } catch (e) {
      setMsg({ kind: 'error', text: 'Delete failed: ' + e.message });
    } finally { setBusy(false); }
  }

  // Screen: warnings, toolbar, vendor table, unclassified dump sites, unclassified haulers, edit pop-up.
  return (
    <div>
      {tableMissing && (
        <div className="card" style={{ padding: '0.6rem 0.8rem', marginBottom: '0.6rem',
                                        background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
          <strong>vendors table not yet created.</strong> Save is disabled until schema-021 is applied.
          {data.hint && <div className="small" style={{ marginTop: '0.15rem', color: '#7f1d1d' }}>{data.hint}</div>}
        </div>
      )}
      {msg && (
        <div className="card" style={{ padding: '0.4rem 0.7rem', marginBottom: '0.5rem',
                                        background: msg.kind === 'ok' ? '#dcfce7' : '#fee2e2',
                                        color:      msg.kind === 'ok' ? '#166534' : '#991b1b' }}>
          {msg.text}
        </div>
      )}

      {/* Toolbar: filter, type filter, + New vendor. */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <input placeholder="Filter vendors…" value={filter}
               onChange={e => setFilter(e.target.value)}
               style={{ flex: 1, minWidth: '12em', padding: '0.35rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: 3 }} />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                style={{ padding: '0.35rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: 3 }}>
          <option value="all">All types</option>
          <option value="dump_site">Dump sites</option>
          <option value="hauler">Haulers</option>
          <option value="both">Both</option>
        </select>
        {canWrite && !tableMissing && (
          <button className="refresh-btn" onClick={() => setEdit({
              display_name: '', vendor_type: 'dump_site', waste_type: '', notes: ''
            })}>+ New vendor</button>
        )}
      </div>

      {/* Vendor table with 12-month QuickBooks spend. */}
      <table>
        <thead>
          <tr>
            <th>Vendor</th>
            <th>Type</th>
            <th>Waste type</th>
            <th className="num">Dump loads</th>
            <th className="num">Haul loads</th>
            <th className="num" title="Trailing 12-month spend from QuickBooks (fuzzy name match; refine with the QBO name field on the vendor).">Spend (12mo)</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {showV.map(v => {
            const q = qboFor(v);
            return (
            <tr key={v.canonical_name}>
              <td>
                {v.display_name}
                {v.qbo_display_name && v.qbo_display_name !== v.display_name && (
                  <div className="small" style={{ color: '#94a3b8' }}>QBO: {v.qbo_display_name}</div>
                )}
              </td>
              <td>{typeChip(v.vendor_type)}</td>
              <td>
                {v.waste_type
                  ? <span style={{ padding: '0.1rem 0.5rem', background: '#eef2ff', color: '#3730a3',
                                    borderRadius: 3, fontSize: '0.85em', fontWeight: 600 }}>{v.waste_type}</span>
                  : (v.vendor_type === 'hauler'
                      ? <span className="small" style={{ color: '#94a3b8' }}>n/a</span>
                      : <span className="small" style={{ color: '#94a3b8' }}>—</span>)}
              </td>
              <td className="num">{v.dest_loads == null ? '—' : Number(v.dest_loads).toLocaleString()}</td>
              <td className="num">{v.haul_loads == null ? '—' : Number(v.haul_loads).toLocaleString()}</td>
              <td className="num" style={{ color: q ? '#0f172a' : '#94a3b8' }}
                  title={q ? 'QBO vendor: ' + q.vendor : 'No matching QBO vendor'}>
                {q ? money(q.amount) : '—'}
              </td>
              <td style={{ textAlign: 'right' }}>
                {canWrite && !tableMissing && (
                  <>
                    <button className="refresh-btn" style={{ padding: '0.15rem 0.5rem' }}
                            onClick={() => setEdit({ ...v })} disabled={busy}>Edit</button>
                    {' '}
                    <button className="refresh-btn" style={{ padding: '0.15rem 0.5rem', color: '#b91c1c' }}
                            onClick={() => del(v)} disabled={busy}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          );})}
          {showV.length === 0 && (
            <tr><td colSpan={7} className="note" style={{ padding: '0.6rem' }}>
              {vendors.length === 0
                ? 'No vendors yet. Click "+ New vendor" or use the "Unclassified" lists below.'
                : 'No vendors match the filter.'}
            </td></tr>
          )}
        </tbody>
      </table>

      {/* Unclassified dump sites: destinations on outgoing loads not yet saved as vendors (one click to classify). */}
      {unclassifiedDests.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <h3 style={{ marginBottom: '0.3rem' }}>Unclassified dump sites</h3>
          <div className="small" style={{ color: '#64748b', marginBottom: '0.3rem' }}>
            Receiving sites seen in <code>loads_outgoing.destination</code> that don't have a vendor row yet.
            Click "Add" to create one and pick its waste type.
          </div>
          <table>
            <thead>
              <tr>
                <th>Destination</th>
                <th className="num">Loads observed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {unclassifiedDests.map(u => (
                <tr key={'d-' + u.canonical_name}>
                  <td>{u.display_name}</td>
                  <td className="num">{Number(u.load_count).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>
                    {canWrite && !tableMissing && (
                      <button className="refresh-btn" style={{ padding: '0.15rem 0.5rem' }}
                              onClick={() => setEdit({
                                display_name: u.display_name,
                                vendor_type: 'dump_site',
                                waste_type: '', notes: ''
                              })}>
                        Add as dump site
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Unclassified haulers: hauler names on outgoing loads not yet saved as vendors. */}
      {unclassifiedHaulers.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <h3 style={{ marginBottom: '0.3rem' }}>Unclassified haulers</h3>
          <div className="small" style={{ color: '#64748b', marginBottom: '0.3rem' }}>
            Trucking companies seen in <code>loads_outgoing.hauler</code> (unit-number suffix stripped)
            that don't have a vendor row yet. Add them to track QBO spend.
          </div>
          <table>
            <thead>
              <tr>
                <th>Hauler</th>
                <th className="num">Loads observed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {unclassifiedHaulers.map(u => (
                <tr key={'h-' + u.canonical_name}>
                  <td>{u.display_name}</td>
                  <td className="num">{Number(u.load_count).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>
                    {canWrite && !tableMissing && (
                      <button className="refresh-btn" style={{ padding: '0.15rem 0.5rem' }}
                              onClick={() => setEdit({
                                display_name: u.display_name,
                                vendor_type: 'hauler',
                                waste_type: '', notes: ''
                              })}>
                        Add as hauler
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / edit pop-up. */}
      {edit && (
        <div className="modal-backdrop" onClick={() => setEdit(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '36em' }}>
            <h3 style={{ marginTop: 0 }}>
              {edit.canonical_name ? 'Edit ' + edit.display_name : 'New vendor'}
            </h3>
            <div style={{ display: 'grid', gap: '0.6rem 0.75rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                <span className="small" style={{ fontWeight: 600 }}>Display name</span>
                <input value={edit.display_name || ''}
                       onChange={e => setEdit({ ...edit, display_name: e.target.value })}
                       placeholder="Matches the destination or hauler string in loads_outgoing"
                       style={{ width: '100%', boxSizing: 'border-box' }} />
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                <span className="small" style={{ fontWeight: 600 }}>Vendor type</span>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {[
                    ['dump_site', 'Dump site',      'Receives outgoing loads. Has a waste type.'],
                    ['hauler',    'Trucking company', 'Delivers loads. No waste type.'],
                    ['both',      'Both',           'Acts as both — has a waste type AND appears as a hauler.']
                  ].map(([val, lbl, hint]) => (
                    <label key={val} title={hint}
                           style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                                    padding: '0.3rem 0.6rem', borderRadius: 4,
                                    border: '1px solid ' + ((edit.vendor_type || 'dump_site') === val ? '#2563eb' : '#cbd5e1'),
                                    background: (edit.vendor_type || 'dump_site') === val ? '#dbeafe' : '#fff',
                                    cursor: 'pointer' }}>
                      <input type="radio" name="vendor_type" value={val}
                             checked={(edit.vendor_type || 'dump_site') === val}
                             onChange={() => setEdit({ ...edit, vendor_type: val })} />
                      <span>{lbl}</span>
                    </label>
                  ))}
                </div>
              </div>
              {(edit.vendor_type || 'dump_site') !== 'hauler' && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                  <span className="small" style={{ fontWeight: 600 }}>Waste type accepted</span>
                  <input list="vendor-waste-types" value={edit.waste_type || ''}
                         onChange={e => setEdit({ ...edit, waste_type: e.target.value })}
                         placeholder="e.g. Sand, Clay, Clay/Stone"
                         style={{ width: '100%', boxSizing: 'border-box' }} />
                  <datalist id="vendor-waste-types">
                    {knownTypes.map(t => <option key={t} value={t} />)}
                  </datalist>
                  <span className="small" style={{ color: '#64748b' }}>
                    Free text — the Overview "By waste type" tiles group on this label. Leave blank for haulers.
                  </span>
                </label>
              )}
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                <span className="small" style={{ fontWeight: 600 }}>QBO vendor name (optional)</span>
                <input value={edit.qbo_display_name || ''}
                       onChange={e => setEdit({ ...edit, qbo_display_name: e.target.value })}
                       placeholder="Exact QuickBooks vendor DisplayName if different from above"
                       style={{ width: '100%', boxSizing: 'border-box' }} />
                <span className="small" style={{ color: '#64748b' }}>
                  Used to match this vendor to QBO spend when the display name doesn't match exactly.
                </span>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                <span className="small" style={{ fontWeight: 600 }}>Notes</span>
                <textarea value={edit.notes || ''} rows={2}
                          onChange={e => setEdit({ ...edit, notes: e.target.value })}
                          style={{ width: '100%', boxSizing: 'border-box',
                                   border: '1px solid #cbd5e1', borderRadius: 3, padding: '0.35rem 0.5rem' }} />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button className="refresh-btn" onClick={() => setEdit(null)} disabled={busy}>Cancel</button>
              <button className="refresh-btn" onClick={save} disabled={busy || !edit.display_name}
                      style={{ background: '#15803d', color: '#fff' }}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
